import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IOfferedSubject } from "../../types";
import {
  selectFailedSubcriptionIds,
  selectFailedSubcriptionSyncIds,
  selectSubcriptionProcessSectionIds,
  selectSubjectsQuotas,
  removeFailedSyncProcessingSubject,
} from "../../reduxSlices/subjectsSlice";
import { getCourseQuota } from "../../utils/quota";
import DesktopSubjectItem from "../DesktopSubjectItem/DesktopSubjectItem";
import Translatable from "../Translatable/Translatable";
import "./OfferOnProcess.css";

interface Props {
  subjects: IOfferedSubject[];
}

// Función helper para crear una clave única de idCurso y anioLectivo
const createProcessKey = (idCurso: number, anioLectivo: number): string => {
  return `${idCurso}-${anioLectivo}`;
};

// Función helper para verificar si un curso está en la lista de procesos
const isCourseInProcess = (
  subcriptionProcessSectionIds: { idCurso: number; anioLectivo: number }[],
  idCurso: number,
  anioLectivo: number,
): boolean => {
  return subcriptionProcessSectionIds.some((item) => item.idCurso === idCurso && item.anioLectivo === anioLectivo);
};

function OfferOnProcess({ subjects }: Props) {
  const dispatch = useDispatch();
  const subcriptionProcessSectionIds = useSelector(selectSubcriptionProcessSectionIds);
  const subjectsQuotas = useSelector(selectSubjectsQuotas);

  // Estado para mantener elementos que están completando su animación
  // Usa claves compuestas "idCurso-anioLectivo" en lugar de solo números
  const [elementsCompletingAnimation, setElementsCompletingAnimation] = useState<Set<string>>(new Set());
  const failedSubcriptionIds = useSelector(selectFailedSubcriptionIds);
  const failedSubcriptionSyncIds = useSelector(selectFailedSubcriptionSyncIds);

  const ANIMATION_DURATION = 3000; // 3 segundos para elementos que terminaron su proceso
  const FAILED_DURATION = 3000; // 3 segundos para elementos fallidos

  // Efecto para manejar elementos que terminan su proceso pero necesitan completar animación
  useEffect(() => {
    // Obtener todas las claves de cursos que están actualmente en proceso
    const currentProcessKeys = new Set(
      subcriptionProcessSectionIds.map((item) => createProcessKey(item.idCurso, item.anioLectivo)),
    );
    // Obtener claves que estaban en proceso anteriormente pero ya no están
    const previousProcessKeys = elementsCompletingAnimation;
    // Encontrar elementos que terminaron su proceso
    const completedElements = Array.from(previousProcessKeys).filter((key) => !currentProcessKeys.has(key));
    // Encontrar elementos que empezaron su proceso
    const newProcessElements = Array.from(currentProcessKeys).filter((key) => !previousProcessKeys.has(key));
    // Agregar nuevos elementos al estado de completando animación
    if (newProcessElements.length > 0) {
      setElementsCompletingAnimation((prev) => {
        const newSet = new Set(prev);
        newProcessElements.forEach((key) => newSet.add(key));
        return newSet;
      });
    }

    // Programar remoción de elementos que terminaron su proceso (después de 17 segundos)
    completedElements.forEach((elementKey) => {
      setTimeout(() => {
        setElementsCompletingAnimation((prev) => {
          const newSet = new Set(prev);
          newSet.delete(elementKey);
          return newSet;
        });
      }, ANIMATION_DURATION);
    });
  }, [subcriptionProcessSectionIds, ANIMATION_DURATION]);

  // Efecto para limpiar IDs del estado failedSubcriptionSyncProcessIds después de 3 segundos
  useEffect(() => {
    failedSubcriptionSyncIds.forEach((item: { idCurso: number; anioLectivo: number }) => {
      setTimeout(() => {
        dispatch(removeFailedSyncProcessingSubject({ idCurso: item.idCurso, anioLectivo: item.anioLectivo }));
      }, FAILED_DURATION);
    });
  }, [failedSubcriptionSyncIds, dispatch, FAILED_DURATION]);

  // Filtrar cursos que están en proceso O completando su animación
  const subjectsInProcess = subjects
    .map((subject) => ({
      ...subject,
      sections: subject.sections.filter((section) => {
        const processKey = createProcessKey(section.idCurso, section.anioLectivo);
        return (
          isCourseInProcess(subcriptionProcessSectionIds, section.idCurso, section.anioLectivo) ||
          elementsCompletingAnimation.has(processKey)
        );
      }),
    }))
    .filter((subject) => subject.sections.length > 0);

  if (subjectsInProcess.length === 0) {
    return null;
  }

  return (
    <div className="offer-on-process-container">
      <div className="offer-on-process-header">
        <h3 className="offer-on-process-title">
          <Translatable as="span" capitalize>Cursos en Proceso de Confirmación</Translatable>
        </h3>
        <span className="offer-on-process-count">
          {subjects
            .map(
              (subject) =>
                subject.sections.filter((section) =>
                  isCourseInProcess(subcriptionProcessSectionIds, section.idCurso, section.anioLectivo),
                ).length,
            )
            .reduce((total, count) => total + count, 0)}
        </span>
      </div>

      <div className="offer-on-process-list">
        {subjectsInProcess.map((subject) =>
          subject.sections.map((section) => {
            const processKey = createProcessKey(section.idCurso, section.anioLectivo);
            const isStillProcessing = isCourseInProcess(
              subcriptionProcessSectionIds,
              section.idCurso,
              section.anioLectivo,
            );
            const isCompletingAnimation = elementsCompletingAnimation.has(processKey) && !isStillProcessing;
            const hasFailed =
              failedSubcriptionIds.some(
                (item) => item.idCurso === section.idCurso && item.anioLectivo === section.anioLectivo,
              ) ||
              failedSubcriptionSyncIds.some(
                (item) => item.idCurso === section.idCurso && item.anioLectivo === section.anioLectivo,
              );
            return (
              <div
                key={section.idCurso}
                className={`offer-on-process-item ${
                  isCompletingAnimation
                    ? hasFailed
                      ? "completing-animation-failed"
                      : "completing-animation-success"
                    : ""
                }`}
              >
                <DesktopSubjectItem
                  idMateria={section.idMateria}
                  id={section.idCurso}
                  subjectId={section.idMateria}
                  section={section.section}
                  subjectData={section.subjectData}
                  quota={getCourseQuota(subjectsQuotas, section.subjectData.reserveData.idC)}
                  pinned={false}
                  creditos={section.creditos}
                  sectionType={section.tipoMateria}
                  turno={section.turno}
                  subject={section}
                  courseInProcess={true}
                />
              </div>
            );
          }),
        )}
      </div>
    </div>
  );
}

export default OfferOnProcess;
